import { useEffect, useState } from 'react';
import { Sparkles, BookOpen, Activity, Brain, PenLine, CheckCircle2 } from 'lucide-react';

/**
 * Animated progress indicator for long AI tasks (~15-25s).
 * Shows a sequence of stage messages with per-stage icons and a rolling progress bar
 * that never fully fills (waiting for real completion). Once `done` is set true,
 * it snaps to 100% and shows a check.
 *
 * props:
 *   active: boolean - whether the task is still running
 *   stages: [{icon?, label, minSec}] - custom stages (optional)
 *   accent: color hex/token (optional, default primary)
 */
const DEFAULT_STAGES = [
  { icon: BookOpen,  label: 'أقرأ ملف المريضة والأدوية…',           minSec: 3 },
  { icon: Activity,  label: 'أحلل اتجاه المزاج والجلسات السابقة…',  minSec: 8 },
  { icon: Brain,     label: 'أستنتج الأنماط والمخاطر…',              minSec: 15 },
  { icon: PenLine,   label: 'أصيغ التقرير السريري بالعربية…',        minSec: 22 },
  { icon: Sparkles,  label: 'أراجع التقرير النهائي وأصقله…',        minSec: 32 },
];

export default function AIProgress({ active, stages = DEFAULT_STAGES, accent = '#8b7ff0' }) {
  const [elapsed, setElapsed] = useState(0);
  const [pct, setPct] = useState(2);

  useEffect(() => {
    if (!active) return;
    setElapsed(0);
    setPct(2);
    const start = Date.now();
    const tick = setInterval(() => {
      const s = (Date.now() - start) / 1000;
      setElapsed(s);
      // Asymptotic curve: approaches 92% but never reaches it while active
      const next = Math.min(92, 92 * (1 - Math.exp(-s / 15)));
      setPct(Math.max(2, next));
    }, 200);
    return () => clearInterval(tick);
  }, [active]);

  // Snap to 100% briefly when task completes
  const [snappedDone, setSnappedDone] = useState(false);
  useEffect(() => {
    if (!active && elapsed > 0) {
      setPct(100);
      setSnappedDone(true);
      const t = setTimeout(() => setSnappedDone(false), 600);
      return () => clearTimeout(t);
    }
  }, [active, elapsed]);

  if (!active && !snappedDone) return null;

  const currentIdx = stages.reduce((idx, s, i) => elapsed >= s.minSec ? i : idx, 0);
  const CurrentIcon = stages[currentIdx]?.icon || Sparkles;

  return (
    <div className="py-4" data-testid="ai-progress">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${accent}22` }}>
          {snappedDone ? (
            <CheckCircle2 className="w-4.5 h-4.5" style={{ color: '#3b8f66' }} />
          ) : (
            <CurrentIcon className="w-4.5 h-4.5 animate-pulse" style={{ color: accent }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>
            {snappedDone ? 'اكتمل!' : (stages[currentIdx]?.label || 'يعمل…')}
          </div>
          <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>
            {snappedDone ? 'تم إنشاء التقرير' : `مضى ${Math.round(elapsed)}ث • خذي نفَسًا…`}
          </div>
        </div>
        <div className="text-[11px] font-black tabular-nums" style={{ color: accent }}>
          {Math.round(pct)}%
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-soft)' }}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out relative overflow-hidden"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${accent}, ${accent}bb)` }}
        >
          {!snappedDone && (
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                animation: 'ai-progress-shine 1.4s linear infinite',
              }}
            />
          )}
        </div>
      </div>

      {/* Stages checklist (subtle) */}
      <div className="mt-3 space-y-1">
        {stages.map((st, i) => {
          const passed = elapsed >= st.minSec;
          const IconRow = st.icon || Sparkles;
          return (
            <div key={i} className="flex items-center gap-2 text-[11px]" style={{ color: passed || snappedDone ? 'var(--text)' : 'var(--text-3)' }}>
              {passed || snappedDone ? (
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: '#3b8f66' }} />
              ) : (
                <IconRow className="w-3 h-3 flex-shrink-0 opacity-40" />
              )}
              <span className={passed || snappedDone ? 'font-semibold' : ''}>{st.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
